/**
 * 通用阻尼最小二乘数值内核。
 *
 * 文件职责：
 * 1. 为装配体配合求解与草图参数化求解提供同一套 Levenberg-Marquardt 步长
 *    与线性方程求解（02 计划 §7.2：单一来源，草图不反向依赖装配体）；
 * 2. 纯数值、无几何语义，不依赖 three.js 与任何求解器输入类型；
 * 3. 原实现来自 `assembly/solver/numeric/StandardMateLeastSquares.ts`，
 *    该文件降级为本模块的兼容 re-export。
 */

/**
 * 计算残差平方和成本。
 *
 * @param residuals 残差数组。
 * @returns 成本。
 */
export function calculateResidualSquareCost(residuals: readonly number[]): number {
    return residuals.reduce((sum, residual) => sum + residual * residual, 0);
}

/**
 * 计算最大残差绝对值。
 *
 * @param residuals 残差数组。
 * @returns 最大绝对残差。
 */
export function calculateMaxAbsoluteResidual(residuals: readonly number[]): number {
    return residuals.reduce((max, residual) => Math.max(max, Math.abs(residual)), 0);
}

/**
 * 解小规模线性方程组（列主元高斯消元）。
 *
 * @param matrix 系数矩阵。
 * @param rhs 右侧向量。
 * @param pivotTolerance 主元容差。
 * @returns 解向量；奇异时返回 null。
 */
export function solveLinearSystemWithPartialPivoting(
    matrix: readonly (readonly number[])[],
    rhs: readonly number[],
    pivotTolerance: number,
): number[] | null {
    const size = rhs.length;
    const augmented = matrix.map((row, rowIndex) => [...row, rhs[rowIndex] ?? 0]);

    for (let columnIndex = 0; columnIndex < size; columnIndex += 1) {
        let pivotRowIndex = columnIndex;
        for (let rowIndex = columnIndex + 1; rowIndex < size; rowIndex += 1) {
            if (Math.abs(augmented[rowIndex]![columnIndex] ?? 0) > Math.abs(augmented[pivotRowIndex]![columnIndex] ?? 0)) {
                pivotRowIndex = rowIndex;
            }
        }

        const pivot = augmented[pivotRowIndex]![columnIndex] ?? 0;
        if (Math.abs(pivot) <= pivotTolerance) {
            return null;
        }
        if (pivotRowIndex !== columnIndex) {
            const currentRow = augmented[columnIndex]!;
            augmented[columnIndex] = augmented[pivotRowIndex]!;
            augmented[pivotRowIndex] = currentRow;
        }

        for (let rowIndex = columnIndex + 1; rowIndex < size; rowIndex += 1) {
            const factor = (augmented[rowIndex]![columnIndex] ?? 0) / pivot;

            if (factor === 0) {
                continue;
            }
            const targetRow = augmented[rowIndex]!;
            const pivotRow = augmented[columnIndex]!;

            /** 当前列按消元定义已归零，只更新后续系数与右端项，避免重复写入。 */
            targetRow[columnIndex] = 0;
            for (let entryIndex = columnIndex + 1; entryIndex <= size; entryIndex += 1) {
                targetRow[entryIndex] = (targetRow[entryIndex] ?? 0)
                    - factor * (pivotRow[entryIndex] ?? 0);
            }
        }
    }

    const solution = Array(size).fill(0);
    for (let rowIndex = size - 1; rowIndex >= 0; rowIndex -= 1) {
        let value = augmented[rowIndex]![size] ?? 0;
        for (let columnIndex = rowIndex + 1; columnIndex < size; columnIndex += 1) {
            value -= (augmented[rowIndex]![columnIndex] ?? 0) * (solution[columnIndex] ?? 0);
        }
        const diagonal = augmented[rowIndex]![rowIndex] ?? 0;
        if (Math.abs(diagonal) <= pivotTolerance) {
            return null;
        }
        solution[rowIndex] = value / diagonal;
    }
    return solution;
}

/**
 * 计算 Levenberg-Marquardt 阻尼最小二乘参数增量。
 *
 * @param jacobian 雅可比矩阵。
 * @param residuals 残差数组。
 * @param damping 阻尼因子。
 * @returns 参数增量；法方程奇异时返回 null。
 */
export function solveDampedLeastSquaresStep(
    jacobian: readonly (readonly number[])[],
    residuals: readonly number[],
    damping: number,
): number[] | null {
    const parameterCount = jacobian[0]?.length ?? 0;
    const normalMatrix = Array.from({length: parameterCount}, () => Array(parameterCount).fill(0));
    const rhs = Array(parameterCount).fill(0);

    jacobian.forEach((row, residualIndex) => {
        const residual = residuals[residualIndex] ?? 0;

        for (let columnIndex = 0; columnIndex < parameterCount; columnIndex += 1) {
            const columnValue = row[columnIndex] ?? 0;

            if (columnValue === 0) {
                continue;
            }
            rhs[columnIndex] -= columnValue * residual;
            const normalRow = normalMatrix[columnIndex]!;

            /** Jacobian 行通常高度稀疏；只累计非零交叉项的上三角，避免无效乘加。 */
            for (let nextColumnIndex = columnIndex; nextColumnIndex < parameterCount; nextColumnIndex += 1) {
                const nextColumnValue = row[nextColumnIndex] ?? 0;

                if (nextColumnValue === 0) {
                    continue;
                }
                normalRow[nextColumnIndex] += columnValue * nextColumnValue;
            }
        }
    });
    for (let parameterIndex = 0; parameterIndex < parameterCount; parameterIndex += 1) {
        normalMatrix[parameterIndex]![parameterIndex] += damping;
        for (let lowerIndex = 0; lowerIndex < parameterIndex; lowerIndex += 1) {
            normalMatrix[parameterIndex]![lowerIndex] = normalMatrix[lowerIndex]![parameterIndex]!;
        }
    }

    return solveLinearSystemWithPartialPivoting(normalMatrix, rhs, 1e-12);
}
